import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Trash2, Eye, ScrollText } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface TocEntry {
  id: string;
  focus_keyword: string;
  output_content: string;
  model_used: string | null;
  created_at: string;
  client_name: string;
  framework_name: string;
}

const TocHistory: React.FC = () => {
  const [entries, setEntries] = useState<TocEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewEntry, setViewEntry] = useState<TocEntry | null>(null);

  const fetchHistory = async () => {
    try {
      const { data: tocs, error } = await supabase
        .from('content_lab_tocs')
        .select('id, focus_keyword, output_content, model_used, created_at, client_id, framework_id')
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (!tocs || tocs.length === 0) { setEntries([]); return; }

      const clientIds = [...new Set(tocs.map(t => t.client_id))];
      const frameworkIds = [...new Set(tocs.map(t => t.framework_id))];

      const [cRes, fRes] = await Promise.all([
        supabase.from('content_lab_clients').select('id, name').in('id', clientIds),
        supabase.from('content_lab_frameworks').select('id, framework_name').in('id', frameworkIds),
      ]);

      const cMap = Object.fromEntries((cRes.data || []).map(c => [c.id, c.name]));
      const fMap = Object.fromEntries((fRes.data || []).map(f => [f.id, f.framework_name]));

      setEntries(tocs.map(t => ({
        ...t,
        client_name: cMap[t.client_id] || 'Unknown',
        framework_name: fMap[t.framework_id] || 'Unknown',
      })));
    } catch (err) {
      console.error(err);
      toast.error('Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchHistory(); }, []);

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this TOC?')) return;
    const { error } = await supabase.from('content_lab_tocs').delete().eq('id', id);
    if (error) toast.error(error.message);
    else { toast.success('Deleted'); fetchHistory(); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">History</h1>
        <p className="text-muted-foreground mt-1">All your generated content guides</p>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground">Loading...</div>
          ) : entries.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              <ScrollText className="h-12 w-12 mx-auto mb-3 opacity-40" />
              <p>No TOCs generated yet</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Framework</TableHead>
                  <TableHead>Focus Keyword</TableHead>
                  <TableHead>Preview</TableHead>
                  <TableHead className="w-20">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map(entry => (
                  <TableRow key={entry.id}>
                    <TableCell className="whitespace-nowrap">
                      {new Date(entry.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>{entry.client_name}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{entry.framework_name}</Badge>
                    </TableCell>
                    <TableCell className="font-medium">{entry.focus_keyword}</TableCell>
                    <TableCell className="text-muted-foreground max-w-xs truncate text-sm">
                      {entry.output_content.slice(0, 100)}...
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => setViewEntry(entry)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(entry.id)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* View Dialog */}
      <Dialog open={!!viewEntry} onOpenChange={() => setViewEntry(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>📝 {viewEntry?.focus_keyword} — Content Guide</DialogTitle>
            <p className="text-sm text-muted-foreground">
              {viewEntry?.client_name} · {viewEntry?.framework_name} · {viewEntry && new Date(viewEntry.created_at).toLocaleString()}
            </p>
          </DialogHeader>
          <div className="prose prose-sm max-w-none dark:prose-invert mt-4">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{viewEntry?.output_content || ''}</ReactMarkdown>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TocHistory;
